import { type FC, type ReactNode } from 'react';

import { pick } from 'lodash-es';

import { isPrimitive, omit } from '@craft/lib/primitives';

import { type QuantitativeSize } from '../../types/sizes';
import { nodeHasRenderableContent, parseElementsByType } from '../../util/elements';
import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';
import { Stack, type StackProps } from '../structural/Stack';
import { type TypographyProps } from '../typography/Typography';

import { EmptyMessageDescription } from './EmptyMessageDescription';
import {
  EmptyMessageDistributedStack,
  type EmptyMessageDistributedStackProps,
} from './EmptyMessageDistributedStack';
import { EmptyMessageTitle } from './EmptyMessageTitle';
import * as types from './types';

const ParseElementProps = ['description', 'descriptionProps', 'title', 'titleProps'] as const;

const parseElements = (
  children: ReactNode,
  props: Pick<EmptyMessageProps, (typeof ParseElementProps)[number]>,
): {
  readonly content?: ReactNode;
  readonly description?: ReactNode;
  readonly title?: ReactNode;
} => {
  const [{ description: parsedDescription, title: parsedTitle }, rest] = parseElementsByType(
    children,
    {
      description: EmptyMessageDescription,
      title: EmptyMessageTitle,
    },
  );
  return {
    content: rest,
    description:
      props.description && isPrimitive(props.description) ? (
        <EmptyMessageDescription {...props.descriptionProps}>
          {props.description}
        </EmptyMessageDescription>
      ) : (
        (props.description ?? parsedDescription)
      ),
    title:
      props.title && isPrimitive(props.title) ? (
        <EmptyMessageTitle {...props.titleProps}>{props.title}</EmptyMessageTitle>
      ) : (
        (props.title ?? parsedTitle)
      ),
  };
};

interface EmptyMessageChildProps extends Omit<
  EmptyMessageDistributedStackProps,
  'maxWidth' | 'width'
> {
  /**
   * Defines the maximum allowed width of the content that sits inside of the {@link EmptyMessage},
   * which includes both the central content (usually an image) and the text (the title and
   * description).  If the 'maxTextWidth' prop is provided, that prop will be favored for the
   * maximum width of the text content, and this 'maxContentWidth' prop will only apply to the
   * central content.
   *
   * This value can be specified as either a percentage (relative to the width of the EmptyMessage)
   * or a fixed pixel value.
   *
   * @default '100%' for the text content and '90%' for the central content.
   */
  readonly maxContentWidth?: QuantitativeSize<'%' | 'px'>;
}

const EmptyMessageChild: FC<EmptyMessageChildProps> = ({
  children,
  maxContentWidth = '100%',
  ...props
}) => (
  <EmptyMessageDistributedStack
    flexShrink={0}
    {...props}
    maxWidth={types.constrainedDimension(maxContentWidth, '100%')}
    width='100%'
  >
    {children}
  </EmptyMessageDistributedStack>
);

interface EmptyMessageContentProps extends Omit<
  EmptyMessageChildProps,
  'flexGrow' | 'flexShrink' | 'justifyContent' | 'maxHeight' | 'minHeight'
> {
  readonly maxContentHeight?: QuantitativeSize<'%' | 'px'>;
  /**
   * Defines the minimum height of the central content area of the {@link EmptyMessage}, which
   * sits in between the text/description (text content) and the action.
   *
   * @default 180px
   */
  readonly minContentHeight?: QuantitativeSize<'px'>;
}

const EmptyMessageContent: FC<EmptyMessageContentProps> = ({
  children,
  contentGap,
  density = types.DefaultEmptyMessageDensity,
  maxContentHeight,
  maxContentWidth = '90%',
  minContentHeight = 180,
  ...props
}) => {
  if (!nodeHasRenderableContent(children)) {
    return null;
  }
  return (
    <EmptyMessageChild
      {...props}
      contentGap={contentGap}
      data-testid='empty-message-content-wrapper'
      density={density}
      flexGrow={100}
      flexShrink={1}
      maxContentWidth={maxContentWidth}
      minHeight={quantitativeSizeToString(minContentHeight, 'px')}
      sx={mergeSx(
        {
          maxHeight:
            typeof maxContentHeight === 'number'
              ? quantitativeSizeToString(maxContentHeight, 'px')
              : (maxContentHeight ?? quantitativeSizeToString(420, 'px')),
          maxWidth: '100%',
        },
        props.sx,
      )}
    >
      <EmptyMessageDistributedStack
        alignItems='center'
        contentGap={contentGap}
        density={density}
        flexShrink={0}
        height='100%'
        justifyContent='center'
        maxHeight='100%'
        maxWidth='100%'
        minHeight='100%'
        sx={{
          '& > *': {
            maxHeight: '100%',
            maxWidth: '100%',
          },
          '& > img': { objectFit: 'contain', width: 'auto' },
        }}
        width='100%'
      >
        {children}
      </EmptyMessageDistributedStack>
    </EmptyMessageChild>
  );
};

interface TextWrapperProps extends EmptyMessageChildProps {
  readonly maxTextWidth?: QuantitativeSize<'%' | 'px'>;
}

const TextWrapper: FC<TextWrapperProps> = ({
  children,
  maxContentWidth,
  maxTextWidth,
  ...props
}) => (
  <EmptyMessageChild
    {...props}
    maxContentWidth={maxTextWidth ?? maxContentWidth}
    sx={mergeSx(props.sx, { '& > *': { maxWidth: '100%', textAlign: 'center', width: '100%' } })}
  >
    {children}
  </EmptyMessageChild>
);

export interface EmptyMessageProps
  extends
    Omit<
      StackProps,
      'alignItems' | 'flexGrow' | 'gap' | 'justifyContent' | 'maxHeight' | 'maxWidth' | 'title'
    >,
    Pick<EmptyMessageDistributedStackProps, 'contentGap' | 'density'>,
    Pick<EmptyMessageContentProps, 'maxContentHeight' | 'minContentHeight'>,
    Pick<EmptyMessageChildProps, 'maxContentWidth'>,
    Pick<TextWrapperProps, 'maxTextWidth'> {
  readonly action?: ReactNode;
  readonly contentPlacement?: 'above' | 'below';
  readonly description?: ReactNode;
  readonly descriptionProps?: Omit<TypographyProps<'div'>, 'children'>;
  /**
   * Defines whether or not a loading indicator should be rendered as an overlay on top of the
   * {@link EmptyMessage} content.
   */
  readonly isLoading?: boolean;
  readonly isScrollable?: boolean;
  /**
   * Defines whether or not the body of the {@link EmptyMessage}, which includes the title,
   * description, content and action, should be justified at the top of the container or vertically
   * centered within the container.
   *
   * @see {@link types.EmptyMessageJustify}
   *
   * @default 'center'
   */
  readonly justifyContent?: types.EmptyMessageJustify;
  readonly title?: ReactNode;
  readonly titleProps?: Omit<TypographyProps<'div'>, 'children'>;
}

export const EmptyMessage = ({
  action,
  children,
  contentGap,
  contentPlacement = 'below',
  density = types.DefaultEmptyMessageDensity,
  isLoading = false,
  isScrollable = true,
  justifyContent = 'center',
  maxContentHeight,
  maxContentWidth,
  maxTextWidth,
  minContentHeight,
  ...props
}: EmptyMessageProps) => {
  const { content, description, title } = parseElements(children, pick(props, ParseElementProps));

  return (
    <Stack
      height='100%'
      padding={types.EmptyMessageDensityProperties[density].padding}
      tabIndex={0}
      width='100%'
      {...omit(props, [...ParseElementProps])}
      alignItems='center'
      className='empty-message'
      data-attr-density={density}
      flexGrow={100}
      justifyContent={justifyContent}
      maxHeight='100%'
      maxWidth='100%'
      position='relative'
      sx={mergeSx(props.sx, {
        overflowX: isScrollable ? 'hidden' : 'visible',
        overflowY: isScrollable ? 'auto' : 'visible',
      })}
    >
      <EmptyMessageDistributedStack
        alignItems='center'
        contentGap={contentGap}
        data-testid='empty-message-content'
        density={density}
        maxHeight='100%'
        maxWidth='100%'
        sx={theme => ({
          '& > *': { maxWidth: '100%' },
          opacity: isLoading ? 0.5 : 1,
          transition: theme.transitions.create('opacity'),
        })}
      >
        {contentPlacement === 'above' && (
          <EmptyMessageContent
            maxContentHeight={maxContentHeight}
            maxContentWidth={maxContentWidth}
            minContentHeight={minContentHeight}
          >
            {content}
          </EmptyMessageContent>
        )}
        <TextWrapper
          contentGap={contentGap}
          data-testid='empty-message-text-container'
          density={density}
          maxContentWidth={maxContentWidth}
          maxTextWidth={maxTextWidth}
        >
          {title}
          {description}
        </TextWrapper>
        {contentPlacement === 'below' && (
          <EmptyMessageContent
            maxContentHeight={maxContentHeight}
            maxContentWidth={maxContentWidth}
            minContentHeight={minContentHeight}
          >
            {content}
          </EmptyMessageContent>
        )}
        {action}
      </EmptyMessageDistributedStack>
    </Stack>
  );
};

EmptyMessage.Title = EmptyMessageTitle;
EmptyMessage.Description = EmptyMessageDescription;
